import { Link, createFileRoute } from '@tanstack/react-router';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});

function PrivacyPage() {
  const language = getLocale();

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Link
          to="/new"
          className="mb-8 inline-block text-sm text-muted-foreground hover:text-foreground"
        >
          {m.privacy_backButton()}
        </Link>

        <article className="prose max-w-none prose-neutral dark:prose-invert">
          {language === 'fr' ? <PrivacyContentFR /> : <PrivacyContentEN />}
        </article>
      </div>
    </div>
  );
}

function PrivacyContentFR() {
  return (
    <>
      <h1>Politique de confidentialité</h1>

      <p>
        <strong>Dernière mise à jour :</strong> <em>13 décembre 2024</em>
      </p>

      <p>
        La présente politique décrit comment <strong>Ovet inc.</strong> (« Ovet
        », « nous », « notre » ou « nos ») recueille, utilise, communique et
        protège les renseignements personnels obtenus lors de l'utilisation de
        l'application de triage numérique <strong>Ovet</strong> (l'« Application
        ») et des services connexes (les « Services »).
      </p>

      <p>
        En utilisant l'Application, vous consentez à la collecte et à
        l'utilisation de vos renseignements conformément à cette politique.
      </p>

      <hr />

      <h2>
        1. Personne responsable de la protection des renseignements personnels
      </h2>

      <p>
        Conformément à la Loi 25, Ovet a désigné un responsable de la protection
        des renseignements personnels :
      </p>

      <p>
        <strong>Nom :</strong> Florence Laforest-Garon
        <br />
        <strong>Courriel :</strong>{' '}
        <a href="mailto:florence@ovet.app">florence@ovet.app</a>
        <br />
        <strong>Adresse postale :</strong> 12-919 av des Érables, Quebec QC G1R
        2M6, Canada
      </p>

      <p>
        Cette personne veille au respect de la Loi 25 et à la mise à jour des
        pratiques internes de gestion des renseignements personnels.
      </p>

      <hr />

      <h2>2. Renseignements personnels recueillis</h2>

      <p>
        Nous recueillons uniquement les renseignements nécessaires à la
        prestation des Services :
      </p>

      <h3>A. Informations de base du propriétaire</h3>
      <ul>
        <li>Nom et prénom</li>
        <li>Courriel</li>
        <li>Numéro de téléphone</li>
      </ul>

      <h3>B. Informations de base sur l'animal</h3>
      <ul>
        <li>Nom de l'animal</li>
        <li>Espèce et race</li>
        <li>Âge approximatif ou date de naissance</li>
      </ul>

      <h3>C. Données du questionnaire de triage</h3>
      <ul>
        <li>
          Réponses fournies concernant les symptômes ou le motif de consultation
        </li>
        <li>Photos ou fichiers facultatifs (si fournis)</li>
      </ul>

      <h3>D. Données techniques</h3>
      <ul>
        <li>
          Type d'appareil, système d'exploitation, adresse IP, journaux
          d'utilisation
        </li>
      </ul>

      <h3>E. Communications par SMS</h3>
      <ul>
        <li>
          Numéro de téléphone utilisé pour l'envoi de liens de questionnaire par
          message texte (SMS)
        </li>
        <li>Statut de livraison des messages</li>
      </ul>
      <p>
        Nous utilisons les SMS uniquement pour transmettre les liens de
        questionnaire demandés par ou au nom du propriétaire de l'animal lors
        d'une visite en clinique vétérinaire. Nous n'envoyons pas de messages
        marketing par SMS. Fréquence : un seul message par visite. Vous pouvez
        vous désabonner à tout moment en répondant STOP à tout message. Des
        frais standard de messagerie et de données peuvent s'appliquer.
      </p>
      <p>
        <strong>
          Les données de consentement SMS ne seront partagées avec aucun tiers.
        </strong>
      </p>

      <p>
        Ces renseignements sont recueillis directement auprès de vous lorsque
        vous remplissez le formulaire de triage.
      </p>

      <hr />

      <h2>3. Fins de la collecte</h2>

      <p>Vos renseignements sont utilisés uniquement pour :</p>
      <ul>
        <li>
          Permettre le traitement du triage et la transmission sécurisée des
          informations à l'équipe vétérinaire
        </li>
        <li>
          Communiquer avec vous au sujet du rendez-vous ou du suivi médical
        </li>
        <li>Améliorer nos Services et l'expérience utilisateur</li>
        <li>Respecter nos obligations légales</li>
      </ul>

      <p>
        <strong>
          Aucun renseignement n'est utilisé à des fins de marketing sans votre
          consentement explicite.
        </strong>
      </p>

      <hr />

      <h2>4. Consentement</h2>

      <p>
        Avant de soumettre vos renseignements, l'Application vous informe
        clairement des finalités de la collecte et requiert votre{' '}
        <strong>consentement explicite</strong>.
      </p>
      <p>
        Vous pouvez en tout temps retirer ce consentement en écrivant à :{' '}
        <a href="mailto:privacy@ovet.app">privacy@ovet.app</a>.
      </p>
      <p>
        Le retrait du consentement peut toutefois limiter votre accès à
        certaines fonctionnalités du triage.
      </p>

      <hr />

      <h2>5. Communication des renseignements</h2>

      <p>Vos renseignements personnels peuvent être communiqués :</p>
      <ul>
        <li>
          Aux cliniques vétérinaires participantes pour assurer la continuité
          des soins
        </li>
        <li>
          À nos fournisseurs technologiques (hébergement, courriels, analytique)
          sous ententes contractuelles de confidentialité
        </li>
        <li>Aux autorités si la loi l'exige</li>
      </ul>

      <p>
        Aucune donnée n'est vendue ni transmise à des tiers à des fins
        publicitaires.
      </p>

      <hr />

      <h2>6. Conservation et destruction des données</h2>

      <p>
        Les renseignements sont conservés uniquement le temps nécessaire pour
        atteindre les fins prévues.
      </p>
      <p>
        Une fois ces fins accomplies, les données sont supprimées ou anonymisées
        de façon sécuritaire conformément aux normes de la{' '}
        <strong>Commission d'accès à l'information du Québec (CAI)</strong>.
      </p>

      <hr />

      <h2>7. Sécurité des renseignements</h2>

      <p>
        Nous appliquons des mesures physiques, administratives et technologiques
        adaptées à la sensibilité des données : chiffrement, contrôle d'accès,
        audits réguliers et stockage sur des serveurs conformes aux lois
        canadiennes et/ou nord-américaines applicables.
      </p>

      <hr />

      <h2>8. Droits des personnes concernées</h2>

      <p>Vous disposez des droits suivants :</p>
      <ul>
        <li>Droit d'accès à vos renseignements personnels</li>
        <li>Droit de rectification de renseignements inexacts ou incomplets</li>
        <li>Droit de retrait du consentement</li>
        <li>Droit à la suppression (dans les limites légales)</li>
        <li>Droit à la portabilité (transfert de vos données)</li>
      </ul>

      <p>
        Pour exercer ces droits, contactez :{' '}
        <a href="mailto:privacy@ovet.app">privacy@ovet.app</a>
      </p>
      <p>Nous répondrons dans les délais prévus par la Loi 25.</p>

      <hr />

      <h2>9. Transfert de renseignements hors du Québec</h2>

      <p>
        Certains renseignements peuvent être hébergés sur des serveurs situés à
        l'extérieur du Québec.
      </p>
      <p>
        Dans ces cas, Ovet s'assure que des mesures de protection équivalentes
        sont en place (clauses contractuelles, chiffrement, conformité
        RGPD/HIPAA lorsque pertinent).
      </p>

      <hr />

      <h2>10. Gestion des incidents de confidentialité</h2>

      <p>
        En cas d'incident de confidentialité présentant un risque de préjudice
        sérieux (ex. : fuite de données), Ovet s'engage à :
      </p>
      <ul>
        <li>
          Aviser la <strong>CAI</strong> et les personnes concernées
        </li>
        <li>Documenter l'incident et les mesures correctives</li>
        <li>Tenir un registre des incidents conformément à la Loi 25</li>
      </ul>

      <hr />

      <h2>11. Mises à jour de la politique</h2>

      <p>
        Cette politique peut être mise à jour pour refléter des changements
        législatifs ou technologiques.
      </p>
      <p>
        La version la plus récente est toujours accessible dans l'Application et
        sur <a href="https://www.ovet.app/privacy">www.ovet.app/privacy</a>.
      </p>

      <hr />

      <h2>12. Nous joindre</h2>

      <p>
        <strong>Ovet inc.</strong>
        <br />
        Courriel : <a href="mailto:privacy@ovet.app">privacy@ovet.app</a>
        <br />
        Site web : <a href="https://www.ovet.app">www.ovet.app</a>
      </p>
    </>
  );
}

function PrivacyContentEN() {
  return (
    <>
      <h1>Privacy Policy</h1>

      <p>
        <strong>Last updated:</strong> <em>December 13, 2024</em>
      </p>

      <p>
        This policy describes how <strong>Ovet inc.</strong> ("Ovet," "we,"
        "our," or "us") collects, uses, discloses, and protects personal
        information obtained through the use of the <strong>Ovet</strong>{' '}
        digital triage application (the "Application") and related services (the
        "Services").
      </p>

      <p>
        By using the Application, you consent to the collection and use of your
        information in accordance with this policy.
      </p>

      <hr />

      <h2>1. Person Responsible for the Protection of Personal Information</h2>

      <p>
        In accordance with Quebec's Law 25, Ovet has designated a person
        responsible for the protection of personal information:
      </p>

      <p>
        <strong>Name:</strong> Florence Laforest-Garon
        <br />
        <strong>Email:</strong>{' '}
        <a href="mailto:florence@ovet.app">florence@ovet.app</a>
        <br />
        <strong>Mailing address:</strong> 12-919 av des Érables, Quebec QC G1R
        2M6, Canada
      </p>

      <p>
        This person ensures compliance with Law 25 and updates internal personal
        information management practices.
      </p>

      <hr />

      <h2>2. Personal Information Collected</h2>

      <p>We collect only the information necessary to provide the Services:</p>

      <h3>A. Owner's Basic Information</h3>
      <ul>
        <li>First and last name</li>
        <li>Email</li>
        <li>Phone number</li>
      </ul>

      <h3>B. Animal's Basic Information</h3>
      <ul>
        <li>Animal's name</li>
        <li>Species and breed</li>
        <li>Approximate age or date of birth</li>
      </ul>

      <h3>C. Triage Questionnaire Data</h3>
      <ul>
        <li>
          Responses provided regarding symptoms or reason for consultation
        </li>
        <li>Optional photos or files (if provided)</li>
      </ul>

      <h3>D. Technical Data</h3>
      <ul>
        <li>Device type, operating system, IP address, usage logs</li>
      </ul>

      <h3>E. SMS Communications</h3>
      <ul>
        <li>
          Phone number used for sending questionnaire links via text message
          (SMS)
        </li>
        <li>Message delivery status</li>
      </ul>
      <p>
        We use SMS solely to deliver questionnaire links requested by or on
        behalf of the pet owner during a veterinary clinic visit. We do not send
        marketing messages via SMS. Frequency: one message per visit. You may
        opt out at any time by replying STOP to any message. Standard message
        and data rates may apply.
      </p>
      <p>
        <strong>
          SMS opt-in data and consent will not be shared with any third parties.
        </strong>
      </p>

      <p>
        This information is collected directly from you when you complete the
        triage form.
      </p>

      <hr />

      <h2>3. Purposes of Collection</h2>

      <p>Your information is used only to:</p>
      <ul>
        <li>
          Enable triage processing and secure transmission of information to the
          veterinary team
        </li>
        <li>
          Communicate with you regarding appointments or medical follow-up
        </li>
        <li>Improve our Services and user experience</li>
        <li>Comply with our legal obligations</li>
      </ul>

      <p>
        <strong>
          No information is used for marketing purposes without your explicit
          consent.
        </strong>
      </p>

      <hr />

      <h2>4. Consent</h2>

      <p>
        Before submitting your information, the Application clearly informs you
        of the purposes of collection and requires your{' '}
        <strong>explicit consent</strong>.
      </p>
      <p>
        You may withdraw this consent at any time by writing to:{' '}
        <a href="mailto:privacy@ovet.app">privacy@ovet.app</a>.
      </p>
      <p>
        However, withdrawal of consent may limit your access to certain triage
        features.
      </p>

      <hr />

      <h2>5. Disclosure of Information</h2>

      <p>Your personal information may be disclosed to:</p>
      <ul>
        <li>Participating veterinary clinics to ensure continuity of care</li>
        <li>
          Our technology providers (hosting, email, analytics) under
          confidentiality agreements
        </li>
        <li>Authorities if required by law</li>
      </ul>

      <p>
        No data is sold or transmitted to third parties for advertising
        purposes.
      </p>

      <hr />

      <h2>6. Data Retention and Destruction</h2>

      <p>
        Information is retained only for as long as necessary to achieve the
        intended purposes.
      </p>
      <p>
        Once these purposes are fulfilled, data is securely deleted or
        anonymized in accordance with the standards of the{' '}
        <strong>Commission d'accès à l'information du Québec (CAI)</strong>.
      </p>

      <hr />

      <h2>7. Information Security</h2>

      <p>
        We implement physical, administrative, and technological measures
        appropriate to the sensitivity of the data: encryption, access control,
        regular audits, and storage on servers compliant with applicable
        Canadian and/or North American laws.
      </p>

      <hr />

      <h2>8. Rights of Data Subjects</h2>

      <p>You have the following rights:</p>
      <ul>
        <li>Right to access your personal information</li>
        <li>Right to rectify inaccurate or incomplete information</li>
        <li>Right to withdraw consent</li>
        <li>Right to deletion (within legal limits)</li>
        <li>Right to portability (transfer of your data)</li>
      </ul>

      <p>
        To exercise these rights, contact:{' '}
        <a href="mailto:privacy@ovet.app">privacy@ovet.app</a>
      </p>
      <p>We will respond within the timeframes provided by Law 25.</p>

      <hr />

      <h2>9. Transfer of Information Outside Quebec</h2>

      <p>Some information may be hosted on servers located outside Quebec.</p>
      <p>
        In such cases, Ovet ensures that equivalent protective measures are in
        place (contractual clauses, encryption, GDPR/HIPAA compliance where
        applicable).
      </p>

      <hr />

      <h2>10. Privacy Incident Management</h2>

      <p>
        In the event of a privacy incident presenting a risk of serious harm
        (e.g., data breach), Ovet commits to:
      </p>
      <ul>
        <li>
          Notify the <strong>CAI</strong> and affected individuals
        </li>
        <li>Document the incident and corrective measures</li>
        <li>Maintain an incident register in accordance with Law 25</li>
      </ul>

      <hr />

      <h2>11. Policy Updates</h2>

      <p>
        This policy may be updated to reflect legislative or technological
        changes.
      </p>
      <p>
        The most recent version is always accessible in the Application and at{' '}
        <a href="https://www.ovet.app/privacy">www.ovet.app/privacy</a>.
      </p>

      <hr />

      <h2>12. Contact Us</h2>

      <p>
        <strong>Ovet inc.</strong>
        <br />
        Email: <a href="mailto:privacy@ovet.app">privacy@ovet.app</a>
        <br />
        Website: <a href="https://www.ovet.app">www.ovet.app</a>
      </p>
    </>
  );
}
